import DataGrid, { Column, SearchPanel, Scrolling } from 'devextreme-react/data-grid';
import { CardAnalytics } from '../../../components/library/card-analytics/CardAnalytics';
import Button from 'devextreme-react/button';
import { useAppSelector } from '../../../contexts/hooks';
import { EReportId } from '../../../types/types.dashboard';
import { useApiDashBoard } from '../../../api/api.dashboards';
import moment from 'moment';
import { useRef, useState } from 'react';
import { useApiOrders } from '../../../api/api.orders';
import { LoadIndicator, TextBox, Tooltip } from 'devextreme-react';
import { useScreenSize } from '../../../utils/media-query';
import { shallowEqual } from 'react-redux';

export const BoardPatient = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const refSearch = useRef<TextBox>(null);
  const { findOrder } = useApiOrders();
  const { isXSmall, isSmall } = useScreenSize();
  const Prm_Ord_ViewOtherLocation = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_ViewOtherLocation);
  const ModuleLocation: boolean = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'ModuleLocation')[0].Cgn_Resultado, shallowEqual) || 'false');

  const { getData } = useApiDashBoard();
  const dashboard = useAppSelector((x) => x.dashboards.dashboards.find((x) => x.Db_Id === EReportId.PatientsValidation));

  if (!dashboard) {
    return <CardAnalytics title='Órdenes por paciente' contentClass='sales' />;
  }

  const handleRefreshClick = () => {
    getData(EReportId.PatientsValidation);
  };

  const handleViewClick = (data: any) => {
    if (!data.Ord_Id) {
      return;
    }
    setLoading(true);
    findOrder(undefined, data.Ord_Id, setLoading).finally(() => {
      refSearch.current?.instance.reset();
    });
  };
  // Función para formatear la fecha con moment - mejorada
  const formatTime = (dateString: string) => {
    return moment(dateString).format('hh:mm A'); // Formato de 12 horas
  };

  // Render personalizado para la columna de hora
  const renderTimeCell = (data: any) => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1px',
        }}>
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: '#495057',
          }}>
          {formatTime(data.value)}
        </span>
      </div>
    );
  };

  // Render personalizado para la columna de documento
  const renderDocumentCell = (data: any) => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}>
        <span
          style={{
            backgroundColor: '#f8f9fa',
            color: '#495057',
            border: '1px solid #dee2e6',
            padding: '2px 6px',
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: '500',
            fontFamily: 'monospace',
          }}>
          {data.value || 'N/A'}
        </span>
      </div>
    );
  };

  // Render personalizado para la columna de sede
  const renderSedeCell = (data: any) => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <span
          style={{
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            border: '1px solid #90caf9',
            padding: '2px 8px',
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: '500',
          }}>
          {data.value || 'N/A'}
        </span>
      </div>
    );
  };
  // Render personalizado para la columna de paciente
  const renderPatientCell = (data: any) => {
    const uid = `patient-${Math.random().toString(36).substr(2, 9)}`;
    const fullName = data.value || 'Sin nombre';
    const isSmallScreen = isXSmall || isSmall;
    const maxLength = isSmallScreen ? 20 : 35; // Límite de caracteres según pantalla
    const truncatedName = fullName.length > maxLength ? `${fullName.substring(0, maxLength)}...` : fullName;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
        id={uid}
        title={fullName.length > maxLength ? fullName : undefined}>
        <span
          style={{
            fontSize: '0.8rem',
            fontWeight: '500',
            color: '#495057',
            lineHeight: '1.2',
            cursor: 'default',
          }}>
          {truncatedName}
        </span>
        <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={`#${uid}`}>
          {data.value}
        </Tooltip>
      </div>
    );
  };

  // Render personalizado para el botón de acción - responsive
  const renderActionCell = (data: any) => {
    const isSmallScreen = isXSmall || isSmall;
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <Button text={isSmallScreen ? '' : 'Ver'} icon={isSmallScreen ? 'eyeopen' : undefined} type='default' stylingMode='contained' onClick={() => handleViewClick(data.data)} hint='Ver detalles del paciente' height={26} />
      </div>
    );
  };
  return (
    <CardAnalytics title={`${dashboard.Data?.length || 0} Pacientes`} contentClass='sales' onRefreshClick={handleRefreshClick} lastUpdate={dashboard.UltimaActualizacion} isLoading={loading}>
      <div
        style={{
          height: '100%',
          position: 'relative',
          maxHeight: isXSmall || isSmall ? '350px' : '100%', // Limitar altura en móviles
          overflow: 'hidden',
        }}>
        <DataGrid
          height={isXSmall || isSmall ? '350px' : '100%'} // Altura específica para móviles
          columnAutoWidth={true}
          allowColumnResizing={false}
          dataSource={dashboard.Data}
          keyExpr='Ord_Id'
          showBorders={false}
          showRowLines={true}
          showColumnLines={false}
          rowAlternationEnabled={true}
          hoverStateEnabled={true}
          onRowPrepared={(e) => {
            if (e.rowType === 'data') {
              e.rowElement.style.transition = 'all 0.2s ease';
              e.rowElement.style.height = isXSmall || isSmall ? '28px' : '32px'; // Altura más compacta en móviles
              e.rowElement.style.minHeight = isXSmall || isSmall ? '28px' : '32px';
            }
          }}
          onCellPrepared={(e) => {
            if (e.rowType === 'data') {
              e.cellElement.style.padding = isXSmall || isSmall ? '2px 4px' : '4px 8px'; // Padding más compacto en móviles
              e.cellElement.style.fontSize = isXSmall || isSmall ? '12px' : '13px'; // Fuente más pequeña en móviles
              e.cellElement.style.lineHeight = '1.2';
            }
          }}>
          <SearchPanel visible={!isXSmall && !isSmall} highlightCaseSensitive={true} /> {/* Ocultar SearchPanel en móviles */}
          <Scrolling mode='virtual' />
          {/* Columna Hora mejorada */}
          <Column dataField='Ord_FechaCreacion' caption='HORA' sortOrder='desc' cellRender={renderTimeCell} allowFiltering={true} width={94} alignment='center' />
          {/* Columna Documento mejorada */}
          <Column dataField='Pc_Identificacion' caption={isXSmall || isSmall ? 'DOC' : 'DOCUMENTO'} cellRender={renderDocumentCell} allowFiltering={true} width={isXSmall || isSmall ? 90 : 120} />
          {/* Columna Sede - visible solo si ModuleLocation y Prm_Ord_ViewOtherLocation son true */}
          {ModuleLocation && Prm_Ord_ViewOtherLocation && (
            <Column 
              dataField='Sd_Nombre' 
              caption={isXSmall || isSmall ? 'SEDE' : 'SEDE'} 
              cellRender={renderSedeCell} 
              allowFiltering={true} 
              width={isXSmall || isSmall ? 100 : 130} 
              alignment='center'
            />
          )}
          {/* Columna Paciente mejorada */}
          <Column caption='PACIENTE' dataField='PacienteCompleto' cellRender={renderPatientCell} allowFiltering={true} calculateCellValue={(data) => data.PacienteCompleto || 'Sin nombre'} />
          {/* Columna % Validación - mantener intacta */}
          <Column
            dataField='PorcentajeValidacion'
            caption={isXSmall || isSmall ? '%' : '% VALIDACIÓN'}
            alignment='center'
            width={isXSmall || isSmall ? 80 : 120}
            cellRender={(data) => {
              const porcentaje = data.value || 0;
              return (
                <div className='meter-container' style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <meter
                    value={porcentaje}
                    min={0}
                    max={100}
                    low={1}
                    high={99}
                    optimum={100}
                    className='animated-meter'
                    style={{
                      width: isXSmall || isSmall ? '40px' : '60px',
                      height: '10px',
                    }}
                  />
                  <span
                    style={{
                      fontSize: isXSmall || isSmall ? '10px' : '11px',
                      fontWeight: '600',
                      minWidth: isXSmall || isSmall ? '20px' : '30px',
                      color: porcentaje === 100 ? '#4caf50' : porcentaje === 0 ? '#f44336' : '#666',
                    }}>
                    {porcentaje}%
                  </span>
                </div>
              );
            }}
          />
          {/* Columna Acciones - responsive */}
          <Column caption='ACCIONES' alignment='center' cellRender={renderActionCell} width={isXSmall || isSmall ? 60 : 90} />
        </DataGrid>

        {/* LoadIndicator mejorado */}
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
            }}>
            <LoadIndicator visible={true} width={30} height={30} />
          </div>
        )}
      </div>
    </CardAnalytics>
  );
};
